import type { LoanRow } from '../lib/loans';
import { loanApi, weeksToRepay } from '../lib/loans';
import { payWeekEndingOf } from '../lib/calendar';
import { formatDate, formatRs, rupeesInWords } from '../lib/money';
import SignatureImage from '../components/SignatureImage';
import StaffStamp from '../components/StaffStamp';

/**
 * The loan receipt — what he signs to say he took the cash, and what he agreed
 * to repay each week. Built like the advance receipt: figures and words (a
 * figure alone can be altered afterwards), the captured signature, or blank
 * lines when he has not signed on the tablet.
 *
 * One A4 carries two copies with a cut line — the office keeps one half and he
 * takes the other.
 */

/** '2026-09-14' → 'September 2026'. */
function monthName(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function Copy({ loan, copy }: { loan: LoanRow; copy: string }) {
  const firstWeek = payWeekEndingOf(loan.given_on);
  return (
    <div className="receipt">
      <div className="rechead">
        <div className="rectitle">Loan receipt</div>
        <div>{formatDate(loan.given_on)} · {copy}</div>
      </div>

      <table>
        <tbody>
          <tr>
            <td style={{ width: '40%' }}>Lent to</td>
            <td><b>{loan.worker_name}</b></td>
          </tr>
          <tr>
            <td>Amount</td>
            <td>
              <b>{formatRs(loan.amount_rs)}</b> (rupees {rupeesInWords(loan.amount_rs)} only)
            </td>
          </tr>
          <tr>
            <td>Purpose</td>
            <td>{loan.reason}</td>
          </tr>
          <tr>
            <td>Repayment</td>
            <td>
              {loan.repay_cycle === 'monthly' ? (
                <>
                  {formatRs(loan.weekly_rs)} a month, taken from his monthly pay on pay day,
                  starting with the pay for {monthName(loan.given_on)} — about{' '}
                  {weeksToRepay(loan.amount_rs, loan.weekly_rs)} months.
                </>
              ) : (
                <>
                  {formatRs(loan.weekly_rs)} a week, taken from his pay, starting with the pay
                  for the week ending {formatDate(firstWeek)} — about{' '}
                  {weeksToRepay(loan.amount_rs, loan.weekly_rs)} full weeks. A week of fewer than six
                  days worked repays that share of it.
                </>
              )}
            </td>
          </tr>
          <tr>
            <td>Handed over by</td>
            <td>
              {loan.issued_by_name ?? '—'}
              {' · asked for by '}{loan.created_by_name}
            </td>
          </tr>
        </tbody>
      </table>

      {loan.signed ? (
        <>
          <div style={{ marginTop: 10, fontSize: 12 }}>Received by (employee):</div>
          <div className="sigprint">
            <SignatureImage path={loanApi.signaturePath(loan.id, loan.signed_at)} />
          </div>
          <div style={{ fontSize: 11 }}>
            Signed on the tablet,{' '}
            {loan.signed_at ? new Date(loan.signed_at).toLocaleString('en-GB') : ''}
          </div>
          <div className="siglines" style={{ marginTop: 10 }}>
            <StaffStamp label="Approved" userId={loan.approved_by} name={loan.approved_by_name} at={loan.approved_at} />
          </div>
        </>
      ) : (
        // Nobody signed on the tablet, so the paper has to carry his line.
        <div className="siglines" style={{ marginTop: 22 }}>
          <div>Received by (employee): __________________________</div>
          <StaffStamp label="Approved" userId={loan.approved_by} name={loan.approved_by_name} at={loan.approved_at} />
        </div>
      )}
    </div>
  );
}

export default function LoanReceipt({ loan }: { loan: LoanRow }) {
  return (
    <div>
      <Copy loan={loan} copy="office copy" />
      <div className="cutline">cut here</div>
      <Copy loan={loan} copy="employee copy" />
    </div>
  );
}
